import { useTranslation } from 'react-i18next';
import { cn } from '@shared/lib/utils';
import { Label, RadioGroup, RadioGroupItem } from '@shared/ui';
import type { ReactNode } from 'react';
import { DecimalUsdtInput } from './decimal-usdt-input';
import type { AutoWithdrawalAmountMode } from '@shared/api/contracts';
import type { AutoWithdrawalRuleDraft } from '@entities/withdrawal';

interface AmountStrategyFieldsProps {
  draft: AutoWithdrawalRuleDraft;
  disabled?: boolean;
  thresholdError?: string;
  reserveError?: string;
  amountModeError?: string;
  onAmountModeChange: (mode: AutoWithdrawalAmountMode) => void;
  onThresholdChange: (value: string) => void;
  onReserveChange: (value: string) => void;
  thresholdId?: string;
  reserveId?: string;
  amountModeId?: string;
  thresholdHint?: ReactNode;
  showThreshold?: boolean;
}

export const AmountStrategyFields = ({
  draft,
  disabled = false,
  thresholdError,
  reserveError,
  amountModeError,
  onAmountModeChange,
  onThresholdChange,
  onReserveChange,
  thresholdId = 'auto-threshold',
  reserveId = 'auto-reserve',
  amountModeId = 'auto-amount-mode',
  thresholdHint,
  showThreshold = true,
}: AmountStrategyFieldsProps) => {
  const { t } = useTranslation('cabinet');

  return (
    <div className="space-y-4">
      {showThreshold && (
        <div className="space-y-2">
          <Label htmlFor={thresholdId}>{t('withdrawal.auto.fields.threshold')}</Label>
          <DecimalUsdtInput
            id={thresholdId}
            value={draft.thresholdAmount}
            assetCode={draft.assetCode}
            disabled={disabled}
            error={thresholdError}
            aria-describedby={`${thresholdId}-hint`}
            onChange={onThresholdChange}
          />
          <div id={`${thresholdId}-hint`}>
            {thresholdHint ?? (
              <p className="text-xs text-muted-foreground">
                {t('withdrawal.auto.hints.strictThreshold', { asset: draft.assetCode })}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="space-y-2">
        <span className="text-sm font-medium">{t('withdrawal.auto.fields.amountStrategy')}</span>
        <RadioGroup
          id={amountModeId}
          value={draft.amountMode}
          disabled={disabled}
          onValueChange={(value) => onAmountModeChange(value as AutoWithdrawalAmountMode)}
          className="gap-2"
        >
          {(['FULL_BALANCE', 'KEEP_RESERVE'] as const).map((mode) => (
            <label
              key={mode}
              className={cn(
                'flex cursor-pointer gap-2.5 overflow-visible rounded-lg border border-border p-3 transition-colors',
                draft.amountMode === mode && 'border-primary shadow-[inset_0_0_0_1px_hsl(var(--primary))]',
                disabled && 'cursor-not-allowed opacity-60',
              )}
            >
              <RadioGroupItem value={mode} className="mt-0.5" />
              <div className="min-w-0 space-y-0.5">
                <span className="block text-sm font-medium">
                  {mode === 'FULL_BALANCE'
                    ? t('withdrawal.auto.amountModes.fullBalanceTitle')
                    : t('withdrawal.auto.amountModes.keepReserveTitle')}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {mode === 'FULL_BALANCE'
                    ? t('withdrawal.auto.amountModes.fullBalanceHint')
                    : t('withdrawal.auto.amountModes.keepReserveHint')}
                </span>
                {mode === 'KEEP_RESERVE' && draft.amountMode === 'KEEP_RESERVE' && (
                  <div className="min-w-0 pt-2">
                    <DecimalUsdtInput
                      id={reserveId}
                      value={draft.reserveAmount}
                      assetCode={draft.assetCode}
                      disabled={disabled}
                      error={reserveError}
                      aria-label={t('withdrawal.auto.fields.reserveBalance')}
                      onChange={onReserveChange}
                    />
                  </div>
                )}
              </div>
            </label>
          ))}
        </RadioGroup>
        {amountModeError && <p className="text-xs text-destructive">{amountModeError}</p>}
      </div>
    </div>
  );
};
